import {
  acceptFollow,
  acceptJoinGroupRequest,
  acceptJoinPlanRequest,
  addFollow,
  addJoinGroupRequest,
  addJoinPlanRequest,
  deleteFollow,
  deleteJoinGroupRequest,
  deleteJoinPlanRequest,
  rejectFollow,
  rejectJoinGroupRequest,
  rejectJoinPlanRequest,
} from '@db/Requests';
import {
  AppQueryCache,
  AppQueryDomain,
  AppQueryKeySegment,
  createQueryKey,
  useAppMutation,
  useAppQueryCache,
} from '@helpers/query';
import { RequestStatus } from '@types';

export enum RequestMutationAction {
  ADD = 'add',
  DELETE = 'delete',
}

export enum RequestResponseMutationAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}

export type FollowRequestMutationVariables = {
  /** Whether the mutation should create or delete the current user's follow request. */
  action: RequestMutationAction;
  /** User being followed or unfollowed. */
  userId: string;
};

export type JoinGroupRequestMutationVariables = {
  /** Whether the mutation should create or delete a join group request. */
  action: RequestMutationAction;
  /** Group being joined or left. */
  groupId: string;
  /** User whose existing group request should be deleted. Defaults to the current user at callers. */
  userId: string;
};

export type JoinPlanRequestMutationVariables = {
  /** Whether the mutation should create or delete a join plan request. */
  action: RequestMutationAction;
  /** Plan being joined or left. */
  planId: string;
  /** User whose existing plan request should be deleted. Defaults to the current user at callers. */
  userId: string;
};

export type FollowRequestResponseMutationVariables = {
  /** Whether to accept or reject the pending follow request. */
  action: RequestResponseMutationAction;
  /** User who requested to follow the current user. */
  userId: string;
};

export type JoinGroupRequestResponseMutationVariables = {
  /** Whether to accept or reject the pending group join request. */
  action: RequestResponseMutationAction;
  /** Group whose join request is being answered. */
  groupId: string;
  /** User who requested to join the group. */
  userId: string;
};

export type JoinPlanRequestResponseMutationVariables = {
  /** Whether to accept or reject the pending plan join request. */
  action: RequestResponseMutationAction;
  /** Plan whose join request is being answered. */
  planId: string;
  /** User who requested to join the plan. */
  userId: string;
};

export function useFollowRequestMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<RequestStatus, Error, FollowRequestMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.REQUESTS, AppQueryKeySegment.FOLLOW_MUTATION),
    mutationFn: async ({ action, userId }) => {
      if (action === RequestMutationAction.DELETE) {
        await deleteFollow(userId);
        return RequestStatus.UNDETERMINED;
      }

      return await addFollow(userId);
    },
    onSuccess: async () => {
      await invalidateFollowMutationQueries(queryCache);
    },
  });
}

export function useFollowRequestResponseMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, FollowRequestResponseMutationVariables>({
    mutationKey: createQueryKey(
      AppQueryDomain.REQUESTS,
      AppQueryKeySegment.FOLLOW_RESPONSE_MUTATION
    ),
    mutationFn: ({ action, userId }) =>
      action === RequestResponseMutationAction.ACCEPT ? acceptFollow(userId) : rejectFollow(userId),
    onSuccess: async () => {
      await invalidateFollowMutationQueries(queryCache);
    },
  });
}

export function useJoinGroupRequestMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<RequestStatus, Error, JoinGroupRequestMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.REQUESTS, AppQueryKeySegment.JOIN_GROUP_MUTATION),
    mutationFn: async ({ action, groupId, userId }) => {
      if (action === RequestMutationAction.DELETE) {
        await deleteJoinGroupRequest(groupId, userId);
        return RequestStatus.UNDETERMINED;
      }

      return await addJoinGroupRequest(groupId);
    },
    onSuccess: async () => {
      await invalidateGroupRequestMutationQueries(queryCache);
    },
  });
}

export function useJoinGroupRequestResponseMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, JoinGroupRequestResponseMutationVariables>({
    mutationKey: createQueryKey(
      AppQueryDomain.REQUESTS,
      AppQueryKeySegment.JOIN_GROUP_RESPONSE_MUTATION
    ),
    mutationFn: ({ action, groupId, userId }) =>
      action === RequestResponseMutationAction.ACCEPT
        ? acceptJoinGroupRequest(groupId, userId)
        : rejectJoinGroupRequest(groupId, userId),
    onSuccess: async () => {
      await invalidateGroupRequestMutationQueries(queryCache);
    },
  });
}

export function useJoinPlanRequestMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<RequestStatus, Error, JoinPlanRequestMutationVariables>({
    mutationKey: createQueryKey(AppQueryDomain.REQUESTS, AppQueryKeySegment.JOIN_PLAN_MUTATION),
    mutationFn: async ({ action, planId, userId }) => {
      if (action === RequestMutationAction.DELETE) {
        await deleteJoinPlanRequest(planId, userId);
        return RequestStatus.UNDETERMINED;
      }

      return await addJoinPlanRequest(planId);
    },
    onSuccess: async (status, { action }) => {
      if (action === RequestMutationAction.DELETE || status === RequestStatus.ACCEPTED) {
        await invalidatePlanRequestMutationQueries(queryCache);
      }
    },
  });
}

export function useJoinPlanRequestResponseMutation() {
  const queryCache = useAppQueryCache();

  return useAppMutation<void, Error, JoinPlanRequestResponseMutationVariables>({
    mutationKey: createQueryKey(
      AppQueryDomain.REQUESTS,
      AppQueryKeySegment.JOIN_PLAN_RESPONSE_MUTATION
    ),
    mutationFn: ({ action, planId, userId }) =>
      action === RequestResponseMutationAction.ACCEPT
        ? acceptJoinPlanRequest(planId, userId)
        : rejectJoinPlanRequest(planId, userId),
    onSuccess: async () => {
      await invalidatePlanRequestMutationQueries(queryCache);
    },
  });
}

async function invalidateFollowMutationQueries(queryCache: AppQueryCache) {
  await Promise.all([
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.FOLLOWS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.USERS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.EXPLORER)),
  ]);
}

async function invalidateGroupRequestMutationQueries(queryCache: AppQueryCache) {
  await Promise.all([
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.GROUPS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.USERS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.EXPLORER)),
  ]);
}

async function invalidatePlanRequestMutationQueries(queryCache: AppQueryCache) {
  await Promise.all([
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.PLANS)),
    queryCache.invalidateQueries(createQueryKey(AppQueryDomain.EXPLORER)),
  ]);
}
